import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Divider,
  Pagination,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';

const DailySalesReport = ({ data, onPageChange, onLimitChange, currentLimit = 50 }) => {
  // Safety check for data
  if (!data || !data.summary) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Daily Business Operations Report</Typography>
        <Typography color="textSecondary">No data available for this report.</Typography>
      </Box>
    );
  }

  // Display location name if available from the invoices
  const locationName = data.invoices && data.invoices.length > 0 && data.invoices[0].location 
    ? data.invoices[0].location.name 
    : null;

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'closed': return 'info';
      case 'in progress': return 'warning';
      case 'open': return 'primary';
      case 'pending': return 'secondary';
      case 'cancelled': return 'error';
      case 'voided': return 'error';
      case 'refunded': case 'refund': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Daily Business Operations Report - {new Date(data.date).toLocaleDateString()}
      </Typography>
      {locationName && (
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Location: {locationName}
        </Typography>
      )}
      {data.generatedAt && (
        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
          Generated: {new Date(data.generatedAt).toLocaleString()}
        </Typography>
      )}

      {/* Operational Overview */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Operational Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography color="inherit" gutterBottom>Completed Sales</Typography>
              <Typography variant="h5" color="inherit">
                ${data.summary.completedSales.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography color="inherit" gutterBottom>Pipeline Value</Typography>
              <Typography variant="h5" color="inherit">
                ${data.summary.pipelineValue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Typography color="inherit" gutterBottom>Work in Progress</Typography>
              <Typography variant="h5" color="inherit">
                ${data.summary.workInProgress.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Value</Typography>
              <Typography variant="h5">${data.summary.totalValue.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Breakdown */}
      <Typography variant="h6" gutterBottom>
        Revenue Collection
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography color="inherit" gutterBottom>Total Collected</Typography>
              <Typography variant="h5" color="inherit">
                ${(data.summary.completedPaid || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="inherit" sx={{ mt: 1, display: 'block' }}>
                All revenue sources
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Customer Payments</Typography>
              <Typography variant="h5">
                ${(data.summary.completedPaidByCustomer || data.summary.totalCustomerPayments || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Direct payments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Insurance Revenue</Typography>
              <Typography variant="h5">
                ${(data.summary.completedPaidByInsurance || data.summary.totalInsuranceRevenue || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Approved claims
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Outstanding Balance</Typography>
              <Typography variant="h5">
                ${(data.operationalInsights?.outstandingBalance || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Uncollected revenue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* KPIs */}
      <Typography variant="h6" gutterBottom>
        Key Performance Indicators
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Conversion Rate</Typography>
              <Typography variant="h5">{data.operationalInsights.conversionRate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Loss Rate</Typography>
              <Typography variant="h5">{data.operationalInsights.lossRate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Invoices</Typography>
              <Typography variant="h5">{data.summary.totalInvoices}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Outstanding Balance</Typography>
              <Typography variant="h5">${data.operationalInsights.outstandingBalance.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Breakdown */}
      {data.statusBreakdown && Object.keys(data.statusBreakdown).length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Status Breakdown
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(data.statusBreakdown).map(([status, stats]) => (
              <Grid item xs={12} sm={6} md={4} key={status}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label={status.toUpperCase()} 
                      color={getStatusColor(status)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="subtitle2">
                      {stats.count} invoice{stats.count !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Typography variant="h6">
                    ${stats.totalAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Paid: ${stats.paidAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Detailed Invoice List */}
      <Typography variant="h6" gutterBottom>All Invoices</Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Customer Paid</TableCell>
              <TableCell align="right">Insurance</TableCell>
              <TableCell align="right">Total Collected</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.invoices && data.invoices.map((invoice) => {
              // Calculate insurance revenue for this invoice
              const insuranceRevenue = (invoice.insuranceClaims || []).reduce((sum, claim) => {
                if (['approved', 'paid'].includes(claim.status?.toLowerCase())) {
                  return sum + (parseFloat(claim.approvedAmount) || 0);
                }
                return sum;
              }, 0);
              
              const customerPaid = parseFloat(invoice.paidAmount || 0);
              const totalCollected = customerPaid + insuranceRevenue;
              
              return (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.invoiceNumber}</TableCell>
                  <TableCell>
                    {invoice.customer 
                      ? `${invoice.customer.firstName} ${invoice.customer.lastName}`
                      : 'N/A'
                    }
                  </TableCell>
                  <TableCell>
                    {invoice.location ? invoice.location.name : 'N/A'}
                  </TableCell>
                  <TableCell align="right">${parseFloat(invoice.totalAmount || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">${customerPaid.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {insuranceRevenue > 0 ? (
                      <Typography color="primary" variant="body2">${insuranceRevenue.toFixed(2)}</Typography>
                    ) : (
                      '$0.00'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      ${totalCollected.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={invoice.status} 
                      color={getStatusColor(invoice.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(invoice.invoiceDate).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              );
            })}
            {(!data.invoices || data.invoices.length === 0) && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary">No invoices found for this date</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {data.pagination && data.pagination.totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {data.pagination.startIndex} to {data.pagination.endIndex} of {data.pagination.totalCount} invoices
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Items per page</InputLabel>
              <Select
                value={currentLimit}
                label="Items per page"
                onChange={(e) => onLimitChange && onLimitChange(parseInt(e.target.value))}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>
            <Pagination 
              count={data.pagination.totalPages} 
              page={data.pagination.currentPage} 
              onChange={(event, page) => onPageChange && onPageChange(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        </Box>
      )}

      {/* Show page size selector even when there's only one page */}
      {data.pagination && data.pagination.totalPages === 1 && data.pagination.totalCount > 50 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Showing all {data.pagination.totalCount} invoices
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Items per page</InputLabel>
            <Select
              value={currentLimit}
              label="Items per page"
              onChange={(e) => onLimitChange && onLimitChange(parseInt(e.target.value))}
            >
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export default DailySalesReport;
